import {Badge, Box, Chip, Fade, IconButton, Menu, MenuItem, Stack, Tooltip, Typography} from "@mui/material";
import {Check, Copy, Delete, History, Settings, User} from "lucide-react";
import {FILE_TYPE_COLORS} from "@/types/FileType.tsx";
import {
  AnalyzerType,
  ApprovalStepExecutionPresenter, useGenerateZyusetuContentStringMutation,
  useGetZyusetuByIdQuery,
  ZyusetuFieldChangedValuePresenter,
  ZyusetuFieldKeyFragmentFragment,
  ZyusetuFieldKeyType,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
} from "@/graphql/graphql";
import {useFormContext, useWatch} from "react-hook-form";
import {FormSchema, UpdateFieldOptions} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {ReactNode, useMemo, useState} from "react";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent";
import {ZyusetuAzureArrayViewerDialog} from "@/components/dialog/ZyusetuAzureArrayViewerDialog.tsx";
import toast from "react-hot-toast";
import {ZyusetuEditorSpecialFunctions} from "@/components/pages/Zyusetu/ZyusetuEditorSpecialFunctions.tsx";
import {
  ZyusetuValueTemplateSelectorDialog
} from "@/components/pages/ZyusetuFields/ZyusetuValueTemplateSelectorDialog.tsx";
import {useGetFormattedValue} from "@/hooks/use-get-formatted-value";
import {ZyusetuDocumentAiTableViewerDialog} from "@/components/dialog/ZyusetuDocumentAiTableViewerDialog.tsx";
import dayjs from "dayjs";
import {ZyusetuDefaultValueDialog} from "@/components/pages/ZyusetuFields/ZyusetuDefaultValueDialog.tsx";
import {ZyusetuEditType} from "@/types/ZyusetuEditType";
import {useUserData} from "@/hooks/use-user-data-provider.ts";

interface ZyusetuFieldWrapperProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema[string],
    options?: UpdateFieldOptions
  ) => Promise<void>;
  onFieldFocus: (
    value: ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
  ) => void;
  children: ReactNode;
  isFirstRow?: boolean;
  zyusetuId: string;
  editType: ZyusetuEditType;
}

export function ZyusetuFieldWrapper({
                                      fieldKey,
                                      updateField,
                                      children,
                                      isFirstRow,
                                      onFieldFocus,
                                      zyusetuId,
                                      editType
                                    }: ZyusetuFieldWrapperProps) {
  const {control} = useFormContext<FormSchema>();
  const watch = useWatch({name: fieldKey.keyName, control});
  
  const {data: zyusetu} = useGetZyusetuByIdQuery({id: zyusetuId});
  
  const generator = useGenerateZyusetuContentStringMutation()
  
  const getFormattedValue = useGetFormattedValue();
  const Chips = useMemo(() => {
    if (
      fieldKey.value === undefined ||
      fieldKey.analyzedPayloads?.length === 0
    ) {
      return null;
    }
    
    const historyValues = fieldKey.changedValues?.map(change =>
      getFormattedValue(fieldKey, change.value)
    ) || [];
    
    return fieldKey.analyzedPayloads
      ?.filter((payload, index, self) => {
        if (!payload.content) return false;
        
        const payloadValue = getFormattedValue(fieldKey, payload.content);
        const currentValue = getFormattedValue(fieldKey, watch?.value, true);
        
        // Skip if it matches current value or exists in history
        return payloadValue !== currentValue && !historyValues.includes(payloadValue) &&
          index === self.findIndex(item =>
            getFormattedValue(fieldKey, item.content) === payloadValue
          ) && payloadValue != ""
      })
      .map((payload) => {
        // ファイルIDからファイルタイプを取得
        const fileType = zyusetu?.getZyusetuById?.files.find(
          file => file.id === payload.analyzedFileId
        )?.fileType;
        
        // ファイルタイプに対応する色を取得
        const backgroundColor = fileType ? FILE_TYPE_COLORS[fileType] : undefined;
        
        return (
          <Chip
            tabIndex={-1}
            key={`rollback-${payload.id}`}
            label={`${getFormattedValue(fieldKey, payload.content)}`}
            size={"small"}
            variant="outlined"
            disabled={editType === ZyusetuEditType.ReadOnly}
            sx={{
              backgroundColor: backgroundColor,
              color: 'rgba(0, 0, 0, 0.7)',
              fontWeight: 'medium',
              '&.Mui-disabled': {
                opacity: 0.7,
                backgroundColor: backgroundColor ? `${backgroundColor}99` : undefined
              }
            }}
            onClick={async () => {
              await updateField(
                fieldKey.id,
                fieldKey.keyName,
                zyusetuValueIntoZodValue(fieldKey, payload.content),
                {
                  payloadId: payload.id,
                }
              );
            }}
            onMouseEnter={async () => {
              onFieldFocus(payload);
            }}
            deleteIcon={<Copy size={15}/>}
            onDelete={() => {
              // clipboard
              navigator.clipboard.writeText(payload.content?.toString() ?? "");
              toast.success("クリップボードにコピーしました", {
                position: "bottom-left",
              });
            }}
          />
        );
      });
  }, [fieldKey, getFormattedValue, watch?.value, editType, updateField, onFieldFocus]);
  
  const [templateOpen, setTemplateOpen] = useState({
    open: false,
    showCreateDialog: false,
  });
  
  const userData = useUserData();
  
  const [defaultValueOpen, setDefaultValueOpen] = useState(false);
  
  const [selectedAzureArray, setSelectedAzureArray] =
    useState<ZyusetuFieldValueAnalyzedPayloadFragmentFragment>();
  
  const [selectedDocumentAiTable, setSelectedDocumentAiTable] = useState<
    ZyusetuFieldValueAnalyzedPayloadFragmentFragment>()
  
  const azureArraryField = useMemo(() => {
    return fieldKey.analyzedPayloads?.find(
      (payload) =>
        payload.type === AnalyzerType.Azure &&
        payload.azureFieldType === "array",
    );
  }, [fieldKey.analyzedPayloads]);
  
  const propertiesPayload = useMemo(() => {
    return fieldKey.analyzedPayloads?.find(
      (payload) =>
        payload.type === AnalyzerType.DocumentAi &&
        payload.properties?.length !== 0,
    )
  }, [fieldKey.analyzedPayloads])
  
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<null | HTMLElement>(null);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<null | HTMLElement>(null);
  const settingsOpen = Boolean(settingsAnchorEl);
  const historyOpen = Boolean(historyAnchorEl);
  
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };
  
  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
  };
  
  const currentValue = watch?.value;
  
  const getApprovalStepExecution = (approvalStepExecutionId: string): ApprovalStepExecutionPresenter | undefined => {
    return zyusetu?.getZyusetuById?.approvalFlow?.steps
      .flatMap(step => step.stepExecution)
      .find(execution => execution.id === approvalStepExecutionId);
  };
  
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>
          <Typography
            variant="formLabel"
            sx={
              isFirstRow
                ? {
                  borderLeft: "4px solid",
                  borderColor: "primary.main",
                  paddingLeft: 2,
                }
                : undefined
            }
          >
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              {fieldKey.label}
              {fieldKey.changedValues && fieldKey.changedValues.length > 0 && (
                <>
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    onClick={(e) => setHistoryAnchorEl(e.currentTarget)}
                  >
                    <Tooltip title="レビュー履歴を表示">
                      {fieldKey.changedValues.some(change => change.value !== currentValue
                      ) ? (
                        <Badge color="warning" variant="dot">
                          <History size={16}/>
                        </Badge>
                      ) : (
                        <History size={16}/>
                      )}
                    </Tooltip>
                  </IconButton>
                  <Menu
                    anchorEl={historyAnchorEl}
                    open={historyOpen}
                    onClose={handleHistoryClose}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        padding: 0,
                        minWidth: 320,
                        maxWidth: 'none',
                        borderRadius: 2,
                      }
                    }}
                  >
                    <Box sx={{px: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        変更依頼
                      </Typography>
                    </Box>
                    <Box sx={{maxHeight: 400, maxWidth: '50vh', overflow: 'auto'}}>
                      {fieldKey.changedValues.map((change: ZyusetuFieldChangedValuePresenter) => {
                        const isCurrentValue = getFormattedValue(fieldKey, change.value) === getFormattedValue(fieldKey, currentValue);
                        const stepExecution = getApprovalStepExecution(change.approvalStepExecutionId);
                        
                        return (
                          <MenuItem
                            key={change.id}
                            onClick={async () => {
                              await updateField(
                                fieldKey.id,
                                fieldKey.keyName,
                                zyusetuValueIntoZodValue(fieldKey, change.value),
                              );
                              handleHistoryClose();
                            }}
                            sx={{
                              display: 'block',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              bgcolor: isCurrentValue ? 'primary.light' : 'transparent',
                              color: isCurrentValue ? 'primary.contrastText' : 'text.primary',
                              '&:hover': {
                                bgcolor: isCurrentValue ? 'primary.main' : 'action.hover',
                              },
                            }}
                          >
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                              <Typography variant="body1" sx={{fontWeight: 'bold', flex: 1}}>
                                {getFormattedValue(fieldKey, change.value)}
                              </Typography>
                              {isCurrentValue && <Check size={16}/>}
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                              <User size={14}/>
                              <Typography variant="caption" color={isCurrentValue ? 'inherit' : 'text.secondary'}>
                                {userData?.user?.id === stepExecution?.user.id ?
                                  <span
                                    style={{color: "blue", fontWeight: 'bold'}}>自分</span> : stepExecution?.user.name}
                              </Typography>
                              <Typography variant="caption" color={isCurrentValue ? 'inherit' : 'text.secondary'}>
                                {dayjs(change.createdAt).format('YYYY/MM/DD HH:mm')}
                              </Typography>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Box>
                  </Menu>
                </>
              )}
              <IconButton
                disabled={editType !== ZyusetuEditType.Edit}
                tabIndex={-1}
                aria-controls={settingsOpen ? 'fade-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={settingsOpen ? 'true' : undefined}
                onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
              >
                <Settings size={'18px'}/>
              </IconButton>
              <Menu
                id="fade-menu"
                MenuListProps={{
                  'aria-labelledby': 'fade-button',
                }}
                anchorEl={settingsAnchorEl}
                open={settingsOpen}
                onClose={handleSettingsClose}
                TransitionComponent={Fade}
              >
                <MenuItem
                  disabled={![ZyusetuFieldKeyType.Number, ZyusetuFieldKeyType.Select, ZyusetuFieldKeyType.String, ZyusetuFieldKeyType.StringAutoComplete].includes(fieldKey.keyType)}
                  onClick={() => {
                    setTemplateOpen({
                      open: true,
                      showCreateDialog: true,
                    });
                    handleSettingsClose()
                  }}>テンプレートを追加</MenuItem>
                    <MenuItem onClick={() => {
                      setDefaultValueOpen(true)
                      handleSettingsClose()
                    }}>
                        <Typography>デフォルト値を設定</Typography>
                    </MenuItem>
              </Menu>
            </Box>
          </Typography>
        </span>
        <Stack direction={"row"} spacing={1}>
          {[
              ZyusetuFieldKeyType.String,
              ZyusetuFieldKeyType.StringAutoComplete,
              ZyusetuFieldKeyType.StringArray,
            ].includes(fieldKey.keyType) &&
            watch?.value && (
              <Tooltip title={"スペースを削除"}>
                <Chip
                  disabled={editType === ZyusetuEditType.ReadOnly}
                  icon={<Delete/>}
                  sx={{p: 0}}
                  tabIndex={-1}
                  size={"small"}
                  onClick={async () => {
                    await updateField(
                      fieldKey.id,
                      fieldKey.keyName,
                      zyusetuValueIntoZodValue(
                        fieldKey,
                        // @ts-expect-error watchの型がおかしい
                        watch.value?.replace(/\s/g, ""),
                      ),
                    );
                  }}
                />
              </Tooltip>
            )}
          {fieldKey.valueTemplate.length !== 0 && (
            <Chip
              tabIndex={-1}
              label={"テンプレートから選択"}
              size={"small"}
              disabled={editType === ZyusetuEditType.ReadOnly}
              onClick={() => {
                setTemplateOpen({
                  open: true,
                  showCreateDialog: false,
                });
              }}
            />
          )}
          {propertiesPayload && !!fieldKey.generator ?
            <Chip
              tabIndex={-1}
              label={'コンテンツをAIで生成する'}
              size={'small'}
              color={"secondary"}
              sx={{
                fontWeight: 'bold'
              }}
              onClick={() => {
                setSelectedDocumentAiTable(propertiesPayload)
              }}
            /> : !!azureArraryField && !!fieldKey.generator && (
            <Chip
              tabIndex={-1}
              label={"コンテンツをAIで生成する"}
              size={"small"}
              color={"secondary"}
              sx={{
                fontWeight: 'bold'
              }}
              onClick={() => {
                setSelectedAzureArray(azureArraryField);
              }}
            />
          )}
        </Stack>
      </Box>
      <Box sx={{
        backgroundColor: fieldKey.changedValues && fieldKey.changedValues.length > 0 || watch?.isReviewed ? 'rgba(255, 152, 0, 0.05)' : 'transparent',
        borderRadius: 1
      }}>
        {children}
      </Box>
      <Box sx={{mt: 1}}>
        {(fieldKey.changedValues?.length ?? 0) > 0 && (fieldKey?.changedValues?.[0]?.value !== currentValue) && (
          <Chip
            key={fieldKey?.changedValues?.[0]?.id}
            size="small"
            tabIndex={-1}
            sx={{mr: 1}}
            icon={<History size={16}/>}
            label={`レビュー有`}
            onClick={(e) => setHistoryAnchorEl(e.currentTarget)}
            color="warning"
          />)
        }
        {Chips}
      </Box>
      {templateOpen?.open && (
        <ZyusetuValueTemplateSelectorDialog
          open={templateOpen?.open}
          editType={editType}
          onClose={() => setTemplateOpen({open: false, showCreateDialog: false})}
          fieldKey={fieldKey}
          showCreateDialog={templateOpen.showCreateDialog}
          onSelect={async (value, templateDescription) => {
            console.log(value, templateDescription);
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(fieldKey, value),
              {
                templateDescription: templateDescription,
              }
            );
            setTemplateOpen({open: false, showCreateDialog: false});
          }}
        />
      )}
      {fieldKey.description && <Typography variant={"caption"}>
        {fieldKey.description}
      </Typography>}
      {fieldKey.value?.templateDescription && <Typography variant={'body2'}>
        テンプレート名：{fieldKey.value.templateDescription}
      </Typography>}
      {
        defaultValueOpen && <ZyusetuDefaultValueDialog
          editType={editType}
          fieldKey={fieldKey} open={defaultValueOpen}
          onClose={() => setDefaultValueOpen(false)}
          updateField={updateField}/>
      }
      {selectedDocumentAiTable && <ZyusetuDocumentAiTableViewerDialog
        open={!!selectedDocumentAiTable}
        onClose={() => setSelectedDocumentAiTable(undefined)}
        payload={selectedDocumentAiTable}
        fieldKey={fieldKey}
        onGenerateItems={async (item) => {
          if (!fieldKey.generator) return;
          const result = await generator.mutateAsync({
            content: item,
            type: fieldKey.generator
          })
          await updateField(
            fieldKey.id,
            fieldKey.keyName,
            zyusetuValueIntoZodValue(fieldKey, result.generateZyusetuContentString),
          )
        }}
      />}
      {selectedAzureArray && (
        <ZyusetuAzureArrayViewerDialog
          open={!!selectedAzureArray}
          onClose={() => setSelectedAzureArray(undefined)}
          payload={selectedAzureArray}/>
      )}
      
      {fieldKey.keyStyle.specialFunctionName && (
        <ZyusetuEditorSpecialFunctions
          fieldKey={fieldKey}
          updateField={updateField}
        />
      )}
    </>
  );
}
